/**
 * LiveRefreshProvider and useLiveRefresh: Utilities for live service version checking and hot reload notifications.
 *
 * This module provides a React context and provider for detecting when a new version of the frontend
 * application has been deployed and is available for refresh. It periodically polls a server endpoint (/.version)
 * to retrieve the latest deployed version identifier, and exposes this, along with the current local version,
 * through context to any component within the provider tree.
 *
 * Usage:
 *   - Wrap your app (or a subtree) in <LiveRefreshProvider>.
 *   - Call useLiveRefresh() in any descendant component to access:
 *       - remoteVersion: string  (deployed version returned by `/.version`)
 *       - serviceVersion: string (local version, usually set at build time)
 *       - isNewVersionAvailable: boolean (true if a newer version is available than the local one)
 *
 * Example:
 *   <LiveRefreshProvider>
 *     <App />
 *   </LiveRefreshProvider>
 *
 *   // Anywhere in App:
 *   const { isNewVersionAvailable } = useLiveRefresh();
 *   if (isNewVersionAvailable) { ... }
 */

import React from 'react';
import { getServiceVersion } from './config';

/**
 * Context value exposed by the LiveRefreshProvider.
 * - remoteVersion: The backend's reported service version.
 * - serviceVersion: The local app's build/version.
 * - isNewVersionAvailable: Whether the backend version differs from the local one.
 */
type LiveRefreshContextValue = {
  remoteVersion: string;
  serviceVersion: string;
  isNewVersionAvailable: boolean;
};

/**
 * Fetches the currently deployed service version by requesting `/.version` from the server.
 * Returns the version string, or '' if the fetch fails.
 */
const fetchRemoteServiceVersion = async (): Promise<string> => {
  try {
    const response = await fetch('/.version', { cache: 'no-store' });
    if (response.status !== 200) {
      return '';
    }

    const data = await response.text();
    return data.trim();
  } catch {
    return '';
  }
};

/**
 * React hook that polls the `/ .version` endpoint every 10 seconds to track the
 * most recent deployed backend version. Returns the remote version string.
 */
const useRemoteVersionRefresher = () => {
  const [remoteVersion, setRemoteVersion] = React.useState<string>('');

  React.useEffect(() => {
    const interval = setInterval(() => {
      fetchRemoteServiceVersion().then((version) => {
        setRemoteVersion(version);
      });
    }, 10_000);

    return () => clearInterval(interval);
  }, []);

  return remoteVersion;
};

/**
 * React context for exposing live-refresh version data.
 */
export const LiveRefreshContext = React.createContext<LiveRefreshContextValue | null>(null);

/**
 * Provider component for LiveRefreshContext.
 * - Polls remote version and compares to the build version from config.
 * - Supplies `remoteVersion`, `serviceVersion`, and a boolean
 *   `isNewVersionAvailable` to context consumers.
 */
export const LiveRefreshProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const remoteVersion = useRemoteVersionRefresher();
  const serviceVersion = getServiceVersion().trim();
  const value = React.useMemo(
    () => ({
      remoteVersion,
      serviceVersion,
      isNewVersionAvailable:
        remoteVersion !== '' && serviceVersion !== '' && remoteVersion !== serviceVersion
    }),
    [remoteVersion, serviceVersion]
  );

  return <LiveRefreshContext.Provider value={value}>{children}</LiveRefreshContext.Provider>;
};

/**
 * React hook to access live-refresh context data.
 */
export const useLiveRefresh = () => {
  const context = React.useContext(LiveRefreshContext);
  if (!context) {
    throw new Error('useLiveRefresh must be used within a LiveRefreshProvider');
  }
  return context;
};
